import { Divider, ListItemIcon, ListItemText, MenuItem, MenuList, Stack, Box, Chip, Typography, Button, Tooltip } from "@mui/material";
import { Account, AccountPopoverFooter, AccountPreview, AccountProps, SignOutButton } from "@toolpad/core";
import { AccountBox, Login, PostAdd, School, Upgrade } from "@mui/icons-material";
import SettingsIcon from "@mui/icons-material/Settings";
import SmartToyIcon from "@mui/icons-material/SmartToy";

import React, { useState } from "react";
import { Avatar, Popover } from "@mui/material";
import { Logout } from "@mui/icons-material";
import { useAuth } from "@/services/Firebase/Authcontext";
import Link from "next/link";

const AccountPopover = ({ useCustomSignOut = false }: { useCustomSignOut?: boolean }) => {
	const { subscription, subscriptionLoading } = useAuth();

	const isDefaultPlan = subscription?.plan?.isDefault ?? true;
	const planName = subscription?.plan?.name ?? "Free";
	const isTrial = subscription?.isTrial ?? false;

	return (
        <Stack direction="column">
            <AccountPreview variant="expanded" />
            {/* Plan Indicator */}
            <Box
				sx={{
					px: 2,
					py: 1.5,
					bgcolor: isDefaultPlan ? "rgba(255, 152, 0, 0.08)" : "rgba(76, 175, 80, 0.08)",
					borderBottom: "1px solid",
					borderColor: "divider",
				}}
			>
				<Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
					<Stack direction="row" spacing={1} alignItems="center">
						<Typography variant="caption" color="text.secondary" fontWeight={500}>
							Current Plan:
						</Typography>
						<Chip
							label={subscriptionLoading ? "Loading..." : isTrial ? planName + " (Trial)" : planName}
							size="small"
							color={isDefaultPlan ? "warning" : "success"}
							sx={{
								fontWeight: 700,
								fontSize: "0.75rem",
							}}
						/>
					</Stack>
					{!subscriptionLoading &&
						(isDefaultPlan ? (
							<Button
								size="small"
								variant="text"
								startIcon={<Upgrade />}
								href="/account/account-settings?tab=ai#upgrade-section"
								sx={{
									fontSize: "0.7rem",
									fontWeight: 700,
									textTransform: "none",
									minWidth: "auto",
									px: 1,
									py: 0.5,
								}}
							>
								Upgrade
							</Button>
						) : (
							<Tooltip title="Manage Subscription">
								<Button
									size="small"
									variant="text"
									startIcon={<SettingsIcon />}
									href="/account/subscription-management"
									sx={{
										fontSize: "0.7rem",
										fontWeight: 700,
										textTransform: "none",
										minWidth: "auto",
										px: 1,
										py: 0.5,
									}}
								/>
							</Tooltip>
						))}
				</Stack>
			</Box>
            <Divider />
            <MenuList dense>
				<MenuItem href="/account" component="a">
					<ListItemIcon>
						<AccountBox />
					</ListItemIcon>
					<ListItemText>Account</ListItemText>
				</MenuItem>

				<MenuItem href="/account/professional-data" component="a">
					<ListItemIcon>
						<PostAdd />
					</ListItemIcon>
					<ListItemText>Professional Info</ListItemText>
				</MenuItem>

				<MenuItem href="/account/subscription-management" component="a">
					<ListItemIcon>
						<School />
					</ListItemIcon>
					<ListItemText>Subscription Management</ListItemText>
				</MenuItem>

				<MenuItem href="/account/ai-usage" component="a">
					<ListItemIcon>
						<SmartToyIcon />
					</ListItemIcon>
					<ListItemText>AI Usage</ListItemText>
				</MenuItem>

				<MenuItem href="/account/account-settings" component="a">
					<ListItemIcon>
						<SettingsIcon />
					</ListItemIcon>
					<ListItemText>Settings</ListItemText>
				</MenuItem>
			</MenuList>
            <Divider />
            {useCustomSignOut ? (
				<Box sx={{ p: 1 }}>
						<Button component={Link} href="/logout" fullWidth startIcon={<Logout />} color="error" variant="text" sx={{ justifyContent: "flex-start" }}>
							Sign Out
						</Button>
				</Box>
			) : (
				<AccountPopoverFooter>
					<SignOutButton />
				</AccountPopoverFooter>
			)}
        </Stack>
    );
};

const AccountPopoverContent = () => <AccountPopover useCustomSignOut={false} />;

export const AccountMenu = ({}: AccountProps) => {
	return (
		<Account
			slots={{
				popoverContent: AccountPopoverContent,
			}}
			slotProps={{
				signInButton: {
					startIcon: <Login />,
					color: "secondary",
					sx: { whiteSpace: "nowrap" },
				},
			}}
		/>
	);
};

export function StandaloneAccountMenu() {
	const { sessionUser } = useAuth();
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

	const handleClick = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const open = Boolean(anchorEl);

	// Get user initials or use default
	const getUserInitials = () => {
		if (sessionUser?.displayName) {
			return sessionUser.displayName
				.split(" ")
				.map((name) => name[0])
				.join("")
				.toUpperCase()
				.slice(0, 2);
		}
		return sessionUser?.email?.[0]?.toUpperCase() || "U";
	};

	return (
		<>
			<Avatar
				src={sessionUser?.photoURL || sessionUser?.displayName || sessionUser?.email || undefined}
				onClick={handleClick}
				sx={{
					cursor: "pointer",
					width: 40,
					height: 40,
					bgcolor: sessionUser?.photoURL ? "transparent" : "primary.main",
				}}
			>
				{!sessionUser?.photoURL && getUserInitials()}
			</Avatar>

			<Popover
				open={open}
				anchorEl={anchorEl}
				onClose={handleClose}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "right",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
			>
				<AccountPopover useCustomSignOut={true} />
			</Popover>
		</>
	);
}
